import { useState } from "react";
import { Eye, EyeSlash, Key, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Full-screen block shown after a user signs in with an admin-issued temporary
 * password (force_password_reset=true). They cannot reach the gallery until
 * they pick a password of their own. On success we call refresh() so the flag
 * clears in context and the protected route unblocks.
 */
export default function ForcePasswordChangeScreen() {
  const { user, refresh, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("New password must be different from your temporary password.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // pull the fresh user (force_password_reset=false) so ProtectedRoute releases the gate
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="suspended-screen" data-testid="force-password-change-screen">
      <div className="suspended-card" style={{ maxWidth: 480 }}>
        <div className="suspended-icon" style={{ background: "rgba(212, 255, 58, 0.12)", color: "var(--lime, #d4ff3a)" }}>
          <Key size={36} weight="duotone" />
        </div>
        <p className="label-uppercase suspended-eyebrow">Temporary password</p>
        <h1 className="suspended-title" style={{ fontSize: "1.75rem" }}>Choose your new password</h1>
        <p className="suspended-sub" style={{ marginBottom: "1.5rem" }}>
          You signed in with a temporary password from the admin
          {user?.email ? <> for <strong>{user.email}</strong></> : null}.
          Pick a password you&rsquo;ll remember to finish setting up your account.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="force-password-change-form" style={{ textAlign: "left" }}>
          <div>
            <label className="label-uppercase block mb-2" htmlFor="fpr-current">Temporary password</label>
            <div className="password-input-wrapper">
              <input
                id="fpr-current"
                type={showCurrent ? "text" : "password"}
                className="input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                data-testid="fpr-current-input"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? "Hide password" : "Show password"}
                data-testid="fpr-current-toggle"
                tabIndex={-1}
              >
                {showCurrent ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>
          </div>

          <div>
            <label className="label-uppercase block mb-2" htmlFor="fpr-new">New password</label>
            <div className="password-input-wrapper">
              <input
                id="fpr-new"
                type={showNew ? "text" : "password"}
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                required
                data-testid="fpr-new-input"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? "Hide password" : "Show password"}
                data-testid="fpr-new-toggle"
                tabIndex={-1}
              >
                {showNew ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
              Minimum 6 characters.
            </p>
          </div>

          <div>
            <label className="label-uppercase block mb-2" htmlFor="fpr-confirm">Confirm new password</label>
            <input
              id="fpr-confirm"
              type={showNew ? "text" : "password"}
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={6}
              required
              data-testid="fpr-confirm-input"
            />
          </div>

          {error && (
            <p className="tnc-modal-error" data-testid="fpr-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-accent w-full"
            disabled={submitting}
            data-testid="fpr-submit"
          >
            {submitting ? "Saving…" : "Save new password"}
          </button>
        </form>

        <button
          type="button"
          onClick={logout}
          className="btn btn-ghost btn-sm"
          style={{ marginTop: "1rem" }}
          data-testid="fpr-logout"
        >
          <SignOut size={14} weight="bold" />
          Sign out instead
        </button>
      </div>
    </div>
  );
}
